import { useState, useCallback } from 'react';

// Image optimization constants
const MAX_DIMENSION = 1200; // Max width/height - covers retina displays at 800px canvas
const JPEG_QUALITY = 0.85; // Good balance of quality and file size

/**
 * Custom hook for processing images with compression and resizing.
 * Handles EXIF orientation, resizing, and JPEG compression.
 *
 * @returns {{
 *   isProcessing: boolean,
 *   error: string,
 *   processImage: (file: File) => Promise<object | null>,
 *   clearError: () => void
 * }}
 */
function useImageProcessor() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const clearError = useCallback(() => {
    setError('');
  }, []);

  const processImage = useCallback(async (file) => {
    setError('');
    setIsProcessing(true);

    if (!file) {
      setIsProcessing(false);
      return null;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      setIsProcessing(false);
      return null;
    }

    try {
      // Create ImageBitmap with EXIF orientation applied
      // This normalizes the image so portrait photos display correctly in canvas
      const bitmap = await createImageBitmap(file, {
        imageOrientation: 'from-image',
      });

      // Calculate target dimensions (resize if larger than MAX_DIMENSION)
      let targetWidth = bitmap.width;
      let targetHeight = bitmap.height;

      if (targetWidth > MAX_DIMENSION || targetHeight > MAX_DIMENSION) {
        const scale = MAX_DIMENSION / Math.max(targetWidth, targetHeight);
        targetWidth = Math.round(targetWidth * scale);
        targetHeight = Math.round(targetHeight * scale);
      }

      // Draw to canvas at target size (resizes large images)
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);

      // Convert to JPEG with optimized quality (always JPEG for smaller size)
      const base64 = canvas.toDataURL('image/jpeg', JPEG_QUALITY);

      bitmap.close();
      setIsProcessing(false);

      return {
        base64,
        width: targetWidth,
        height: targetHeight,
        aspectRatio: targetWidth / targetHeight,
      };
    } catch (err) {
      // Fallback for older browsers: use FileReader without EXIF correction
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setIsProcessing(false);
          resolve({
            base64: e.target.result,
            width: null,
            height: null,
            aspectRatio: null,
          });
        };
        reader.onerror = () => {
          setError('Failed to process image');
          setIsProcessing(false);
          resolve(null);
        };
        reader.readAsDataURL(file);
      });
    }
  }, []);

  return {
    isProcessing,
    error,
    processImage,
    clearError,
  };
}

export default useImageProcessor;
